/**
 * Permissions and role-to-permission seed for the RBAC refactor.
 *
 * This file is the single source of truth for:
 *   - the permission catalog (code-defined; new permissions require a code change)
 *   - the system role list (seeded into the `roles` DB table)
 *   - the initial role-to-permission mapping (seeded into `role_permissions`)
 *
 * At runtime the DB tables are authoritative. This file is used on first boot
 * (to seed empty tables) and as the baseline for the snapshot test that guards
 * against accidental matrix drift.
 *
 * See openspec/changes/add-permissions-rbac/ for the design.
 */

/** Wildcard sentinel: granted to sysadmin; resolver expands it to every permission. */
export const WILDCARD_PERMISSION = '*';
export type WildcardPermission = typeof WILDCARD_PERMISSION;

/**
 * Closed enum of permissions. A permission exists if and only if code gates on it
 * via `@RequirePermissions(...)` on the backend or `can(...)` on the frontend.
 *
 * Adding a permission:
 *   1. Append it here (keep grouping consistent — group by feature area).
 *   2. Assign it to at least one system role in `ROLE_PERMISSIONS_SEED` below.
 *   3. Update the snapshot test by running it with --update.
 *
 * Naming convention: `<feature>.<action>` (lowercase, snake_case within segments).
 */
export const PERMISSIONS = [
  // Work orders
  'workorder.read',
  'workorder.write',
  'workorder.approve',
  'workorder.complete',
  'workorder.assign',

  // Insights / reporting
  'insights.read',
  'content.write',
  'rag.write',
  'feedback.read',
  'automations.read',
  'integrations.read',
  'notifications.broadcast',

  // Dashboard config
  'dashboard.config.write',

  // Executive dashboard
  'executive.view',

  // Admin system
  'admin.system.manage',

  // Access control (used by add-access-control-admin)
  'users.manage',
  'roles.manage',
  'permissions.manage',
  'feature_matrix.read',
  'feature_matrix.write',

  // Offline field mode (field-mode-v1)
  'offline.config.write',
  'offline.conflict.read',
  'offline.conflict.resolve',
  'offline.support.read',
  'offline.support.reply',
  'offline.emergency.grant',

  // Fleet (LBRE A→G workflow). Grants visibility into fleet nav items
  // (vehicles, fleet-calendar, fleet-dashboards, approvals-queue, vendors).
  // Held by the four LBRE fleet roles plus supervisor/admin/sysadmin so any
  // user with operational fleet duties can navigate the surface.
  'fleet.access',

  // Billing tracking (LBRE Finance — add-billing-tracking). Gate the finance
  // console and its process actions: generate/review statements, assign
  // unallocated cost, post corrections, close periods, export.
  'billing.access',
  'billing.statements.generate',
  'billing.allocate',
  'billing.corrections.post',
  /** Approve/reject a closed-period Credit/Write-off correction request —
   *  distinct from `billing.corrections.post` (which only lets someone post
   *  an open-period correction, or *request* a closed-period one). Per the
   *  LBRE-012 user journey, this is the one action meant to actually
   *  differentiate Finance Director from Financial Analyst. */
  'billing.corrections.approve',
  'billing.period.close',
  /** Reopening is NOT the inverse of closing, and must not share its
   *  permission. It removes the period opened after this one, deletes that
   *  period's statements, and clears the billed stamps a close wrote into
   *  Maximo — so one click can unwind a month for everybody. Analysts hold
   *  `billing.period.close` on purpose (they close during UAT); giving them
   *  reopen too meant a tester could make the open month vanish, which is
   *  what UAT reported on 10-20-26 as "June disappeared, May became open". */
  'billing.period.reopen',
  'billing.export',
  /** Edit journey/step wording, see every tester's progress and delete
   *  anyone's comment. Reading the journeys and commenting needs no
   *  permission at all — acceptance testing is open to any signed-in user, so
   *  those routes are guarded by authentication alone rather than by a
   *  permission nobody would ever withhold. */
  'uat.manage',
  'billing.projects.manage',
  'billing.default-pta.manage',
  'billing.rules.manage',
  'billing.rates.manage',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/**
 * System role keys. These nine are seeded on first boot with `isSystem=true` in the
 * `roles` table and cannot be renamed or deleted via the admin UI.
 *
 * `vendor_contact` is the flat legacy vendor role kept for backwards compatibility
 * while the three-role vendor hierarchy (`vendor_admin` / `vendor_supervisor` /
 * `vendor_tech`) rolls out per openspec/changes/add-vendor-p2p-flow.
 *
 * Custom (non-system) roles are created at runtime via the admin UI in the
 * follow-up change `add-access-control-admin` and have their own keys.
 */
export const SYSTEM_ROLES = [
  {
    key: 'technician',
    label: 'Technician',
    description: 'Field technician — executes assigned work orders.',
  },
  {
    key: 'supervisor',
    label: 'Supervisor',
    description: 'Oversees crews, approves timecards, reviews reports.',
  },
  {
    key: 'admin',
    label: 'Administrator',
    description: 'Internal platform admin — user management, configuration, integrations.',
  },
  {
    key: 'sysadmin',
    label: 'System Administrator',
    description: 'Full wildcard access. Cannot be modified via UI.',
  },
  {
    key: 'vendor_contact',
    label: 'Vendor Contact (legacy)',
    description: 'Legacy flat vendor role. Being replaced by the vendor_* hierarchy.',
  },
  {
    key: 'vendor_admin',
    label: 'Vendor Admin',
    description: 'External vendor organization admin — manages their team and billing.',
  },
  {
    key: 'vendor_supervisor',
    label: 'Vendor Supervisor',
    description: 'External vendor supervisor — dispatches and reviews their techs.',
  },
  {
    key: 'vendor_tech',
    label: 'Vendor Technician',
    description: 'External vendor field technician.',
  },
  {
    key: 'customer_operator',
    label: 'Customer Operator',
    description: 'Fleet / facility customer operating assigned vehicles or assets.',
  },
  {
    key: 'garage_coordinator',
    label: 'Garage Coordinator',
    description: 'LBRE fleet coordinator — schedules staging appointments and contacts customers.',
  },
  {
    key: 'garage_lead',
    label: 'Garage Lead',
    description: 'Approves fleet repair estimates under $500 directly; escalates higher amounts to the customer.',
  },
  {
    key: 'garage_cls',
    label: 'Garage CLS',
    description: 'Washes and refuels returned vehicles before customer pickup.',
  },
  {
    key: 'fleet_technician',
    label: 'Fleet Technician',
    description: 'Evaluates vehicles, coordinates vendor handoffs, schedules return appointments.',
  },
  {
    key: 'p2p_reviewer',
    label: 'P2P Reviewer',
    description: 'Procure-to-pay reviewer — three-way match, invoice validation.',
  },
  {
    key: 'executive',
    label: 'Executive',
    description: 'Leadership view — read-only operational parity with supervisor plus executive dashboard.',
  },
  {
    key: 'executive:lbre',
    label: 'Executive · LBRE',
    description: 'Executive scoped to LBRE campus facilities.',
  },
  {
    key: 'executive:som',
    label: 'Executive · School of Medicine',
    description: 'Executive scoped to Stanford School of Medicine.',
  },
  {
    key: 'executive:slac',
    label: 'Executive · SLAC',
    description: 'Executive scoped to SLAC National Accelerator Laboratory.',
  },
  {
    key: 'lbre_finance',
    label: 'LBRE Finance',
    description: 'LBRE Financial department — billing console: statements, corrections, period close, export.',
  },
  {
    key: 'financial_analyst',
    label: 'Financial Analyst',
    description: 'LBRE Finance role from the POC user journeys (LBRE-011/LBRE-012) — configures Billing Projects/PTA allocations and runs Trial Billing, Adjustments, and the GL Feeder File.',
  },
  {
    key: 'finance_director',
    label: 'Finance Director',
    description: 'LBRE Finance role from the POC user journeys (LBRE-011/LBRE-012) — same journeys as Financial Analyst, plus sole authorization over post-posting Corrections.',
  },
  {
    key: 'uat_admin',
    label: 'UAT Admin',
    description: 'Runs the acceptance-testing cycle: maintains the journey and step wording, sees every tester\'s progress and verdicts, and moderates comments. Deliberately separate from billing duty so a BA or project lead can hold it.',
  },
] as const satisfies ReadonlyArray<{ key: string; label: string; description: string }>;

export type SystemRoleKey = (typeof SYSTEM_ROLES)[number]['key'];

/**
 * Initial role-to-permission assignments seeded into `role_permissions` on first
 * boot. After seed, the DB is authoritative and the admin UI edits it directly;
 * this constant is NOT re-read at runtime. It is the baseline for the snapshot
 * test that surfaces every matrix change as a PR diff.
 *
 * `sysadmin` maps to the wildcard sentinel; the resolver expands it to the full
 * permission set. Admin UI cannot modify sysadmin's row.
 */
export const ROLE_PERMISSIONS_SEED: Record<
  SystemRoleKey,
  ReadonlyArray<Permission | WildcardPermission>
> = {
  technician: ['workorder.read', 'workorder.write', 'workorder.complete'],

  supervisor: [
    'workorder.read',
    'workorder.write',
    'workorder.approve',
    'workorder.complete',
    'workorder.assign',
    'insights.read',
    'content.write',
    'rag.write',
    'feedback.read',
    'automations.read',
    'integrations.read',
    'notifications.broadcast',
    'fleet.access',
  ],

  admin: [
    'workorder.read',
    'workorder.write',
    'workorder.approve',
    'workorder.complete',
    'workorder.assign',
    'insights.read',
    'content.write',
    'rag.write',
    'feedback.read',
    'automations.read',
    'integrations.read',
    'notifications.broadcast',
    'dashboard.config.write',
    'admin.system.manage',
    'users.manage',
    'roles.manage',
    'permissions.manage',
    'feature_matrix.read',
    'feature_matrix.write',
    'offline.config.write',
    'offline.conflict.read',
    'offline.conflict.resolve',
    'offline.support.read',
    'offline.support.reply',
    'offline.emergency.grant',
    'fleet.access',
    'billing.access',
    'billing.statements.generate',
    'billing.allocate',
    'billing.corrections.post',
    'billing.corrections.approve',
    'billing.period.close',
    'billing.period.reopen',
    'billing.export',
    'billing.projects.manage',
    'billing.default-pta.manage',
    'billing.rules.manage',
    'billing.rates.manage',
  ],

  sysadmin: [WILDCARD_PERMISSION],

  vendor_contact: ['workorder.read', 'fleet.access'],

  vendor_admin: ['workorder.read', 'workorder.write', 'workorder.assign', 'fleet.access'],

  vendor_supervisor: [
    'workorder.read',
    'workorder.write',
    'workorder.approve',
    'workorder.complete',
    'workorder.assign',
    'fleet.access',
  ],

  vendor_tech: ['workorder.read', 'workorder.complete', 'fleet.access'],

  customer_operator: ['workorder.read', 'fleet.access'],

  // Fleet roles (LBRE A→G workflow). Garage coordinator schedules and contacts;
  // Garage lead has approval authority for `<$500` proposals; CLS handles wash+refuel;
  // Fleet tech evaluates and coordinates handoffs. All four hold `fleet.access`
  // so the fleet nav items become visible (any-of with `workorder.approve` for
  // supervisor/admin parity).
  garage_coordinator: ['workorder.read', 'workorder.write', 'workorder.assign', 'fleet.access'],
  garage_lead: ['workorder.read', 'workorder.write', 'workorder.approve', 'workorder.assign', 'fleet.access'],
  garage_cls: ['workorder.read', 'workorder.write', 'workorder.complete', 'fleet.access'],
  fleet_technician: ['workorder.read', 'workorder.write', 'workorder.complete', 'workorder.assign', 'fleet.access'],

  // New role — empty until the P2P slice wires permissions onto its endpoints.
  p2p_reviewer: ['workorder.read', 'feedback.read', 'insights.read'],

  // Executive — supervisor parity + executive.view
  executive: [
    'workorder.read',
    'workorder.write',
    'workorder.approve',
    'workorder.complete',
    'workorder.assign',
    'insights.read',
    'content.write',
    'rag.write',
    'feedback.read',
    'automations.read',
    'integrations.read',
    'notifications.broadcast',
    'executive.view',
  ],

  // Site-scoped executive variants inherit from executive (same permission set)
  'executive:lbre': [
    'workorder.read',
    'workorder.write',
    'workorder.approve',
    'workorder.complete',
    'workorder.assign',
    'insights.read',
    'content.write',
    'rag.write',
    'feedback.read',
    'automations.read',
    'integrations.read',
    'notifications.broadcast',
    'executive.view',
  ],

  'executive:som': [
    'workorder.read',
    'workorder.write',
    'workorder.approve',
    'workorder.complete',
    'workorder.assign',
    'insights.read',
    'content.write',
    'rag.write',
    'feedback.read',
    'automations.read',
    'integrations.read',
    'notifications.broadcast',
    'executive.view',
  ],

  'executive:slac': [
    'workorder.read',
    'workorder.write',
    'workorder.approve',
    'workorder.complete',
    'workorder.assign',
    'insights.read',
    'content.write',
    'rag.write',
    'feedback.read',
    'automations.read',
    'integrations.read',
    'notifications.broadcast',
    'executive.view',
  ],

  // LBRE Finance — billing console + read access to operational cost context.
  lbre_finance: [
    'workorder.read',
    'insights.read',
    'billing.access',
    'billing.statements.generate',
    'billing.allocate',
    'billing.corrections.post',
    'billing.corrections.approve',
    'billing.period.close',
    'billing.period.reopen',
    'billing.export',
    'billing.projects.manage',
    'billing.default-pta.manage',
    'billing.rules.manage',
    'billing.rates.manage',
  ],

  // POC roles from the LBRE user journeys deck (LBRE-011/LBRE-012).
  //
  // The two carried one split — closed-period correction approval and closing
  // the month were the Finance Director's alone (9-16-26). For the POC the
  // client asked for the analyst to hold everything the director does
  // (10-2-26): testers were hitting "Missing required permission" on actions
  // they were asked to validate, and chasing a second account to finish a
  // journey was costing more than the separation was proving.
  //
  // `billing.corrections.approve` came back on 10-21-26: LBRE could not work
  // out how a Director approves a correction, because the Approve control was
  // showing for the analyst who had raised the request. That is the one
  // separation the Corrections flow is built to demonstrate — an analyst
  // requests, a Director authorises — so the analyst no longer holds it, and
  // LBRE-012-6's "the analyst cannot approve" is true again.
  //
  // `billing.period.close` is deliberately still granted to both: nobody has
  // asked for closing to go back to the Director alone, and taking it away
  // would stop the analyst testers closing a period mid-journey.
  financial_analyst: [
    'workorder.read',
    'insights.read',
    'billing.access',
    'billing.statements.generate',
    'billing.allocate',
    'billing.corrections.post',
    'billing.period.close',
    'billing.export',
    'billing.projects.manage',
    'billing.default-pta.manage',
    'billing.rules.manage',
    'billing.rates.manage',
  ],

  finance_director: [
    'workorder.read',
    'insights.read',
    'billing.access',
    'billing.statements.generate',
    'billing.allocate',
    'billing.corrections.post',
    'billing.corrections.approve',
    'billing.period.close',
    'billing.period.reopen',
    'billing.export',
    'billing.projects.manage',
    'billing.default-pta.manage',
    'billing.rules.manage',
    'billing.rates.manage',
  ],

  // Runs the test cycle without holding billing duty.
  uat_admin: ['uat.manage'],
};

/**
 * Resolve a set of role keys to the union of their seeded permissions, expanding
 * the wildcard sentinel. This is used for the snapshot test and as a reference
 * implementation; at runtime the backend resolver reads from the DB.
 */
export function resolveSeedPermissions(
  roleKeys: ReadonlyArray<SystemRoleKey>,
): ReadonlySet<Permission> {
  const out = new Set<Permission>();
  for (const key of roleKeys) {
    const grants = ROLE_PERMISSIONS_SEED[key] ?? [];
    for (const grant of grants) {
      if (grant === WILDCARD_PERMISSION) {
        for (const p of PERMISSIONS) out.add(p);
      } else {
        out.add(grant);
      }
    }
  }
  return out;
}
